// @system — GDPR-compliant cookie consent banner
// Shown once to new visitors; stores preference in localStorage.
// Accepts: essential only, or all cookies. Preference persists for 1 year.
// Also logs the consent choice server-side via POST /api/gdpr/consent (Art. 7).
// @custom — override CONSENT_KEY if the product slug needs a unique key.

import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Cookie, X, Check } from 'lucide-react'
import { cn } from '@/app/lib/@system/utils'

const CONSENT_KEY = 'cookie_consent'
/** CSS custom property (on <html>) carrying the visible banner's height; layouts pad by it. */
export const CONSENT_BANNER_HEIGHT_VAR = '--consent-banner-height'
/**
 * Same-tab consent change event (the `storage` event only fires in OTHER tabs). Dispatched on
 * `window` by this banner and by /cookie-consent.js; `useCookieConsent` listens so consent-gated
 * scripts (components/@system/Analytics) start the moment the visitor accepts.
 */
export const CONSENT_CHANGE_EVENT = 'cookie-consent-change'
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000 // 1 year

/** @typedef {'all' | 'essential'} ConsentValue */

/** @returns {{ value: ConsentValue, ts: number } | null} */
function loadConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || !parsed.ts) return null
    if (Date.now() - parsed.ts > CONSENT_TTL_MS) {
      localStorage.removeItem(CONSENT_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

/** @param {ConsentValue} value */
function saveConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ value, ts: Date.now() }))
  } catch {}
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: value }))
  } catch {}
}

/**
 * Launch-contract markers (rule 5): `data-consent-banner` on the banner root and
 * `data-consent-accept` on its accept button. The static /cookie-consent.js banner (customer-owned
 * `client/public`, so a template sync does not refresh it) is stamped here as well, so a product on
 * an older copy of that script still exposes the markers through this @system component.
 * @param {HTMLElement} banner
 */
function stampConsentMarkers(banner) {
  banner.setAttribute('data-consent-banner', '')
  const accept = banner.querySelector('#cc-accept')
  if (accept) accept.setAttribute('data-consent-accept', '')
}

// Fire-and-forget: log consent to server for GDPR Art. 7 audit trail.
// Failures are silently swallowed — client-side consent is already saved.
/** @param {ConsentValue} value */
function logConsentToServer(value) {
  try {
    const IS_TUNNEL = !window.location.origin.includes('localhost')
    const API_BASE = IS_TUNNEL ? `${window.location.origin}/api` : '/api'
    fetch(`${API_BASE}/gdpr/consent`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ consent_value: value }),
    }).catch((err) => console.warn('[CookieConsentBanner] consent could not be recorded', err))
  } catch {}
}

/**
 * CookieConsentBanner
 *
 * Renders a fixed bottom banner until the user makes a choice.
 * After accepting/rejecting, the banner disappears and the choice is persisted.
 *
 * Usage: mount once at the app root (App.jsx). No props required.
 *
 * @param {WithClassName} props - `className`: additional CSS classes for the banner
 */
export function CookieConsentBanner({ className }) {
  const [consent, setConsent] = useState(/** @type {ConsentValue | null} */ (null))
  const [visible, setVisible] = useState(false)
  const bannerRef = useRef(/** @type {HTMLDivElement | null} */ (null))

  // The banner is fixed to the bottom edge; publish its height as `--consent-banner-height` on
  // <html> while it is shown. styles/@system/general.css derives `--viewport-height` from it and
  // Tailwind's h-screen / min-h-screen / max-h-screen resolve to that token, so every
  // viewport-sized layout (PageLayout column, Sidebar, DashboardLayout, AppLayout) ends above the
  // banner and content pinned low — footer links, an empty state's call to action, a focused
  // control — is never covered (WCAG 2.2 2.4.11 Focus Not Obscured, 2.5.8 Target Size clearance).
  // Padding <body> did not achieve that: an h-screen child kept the column at full height.
  useEffect(() => {
    const el = bannerRef.current
    if (!el) return undefined
    const apply = () => { document.documentElement.style.setProperty(CONSENT_BANNER_HEIGHT_VAR, `${el.offsetHeight}px`) }
    apply()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null
    ro?.observe(el)
    return () => {
      ro?.disconnect()
      document.documentElement.style.removeProperty(CONSENT_BANNER_HEIGHT_VAR)
    }
  }, [visible, consent])

  useEffect(() => {
    const stored = loadConsent()
    const staticBanner = document.getElementById('cc-banner')
    if (stored) {
      setConsent(stored.value)
      setVisible(false)
    } else if (staticBanner) {
      // Static cookie-consent.js banner already in the DOM — defer to it to avoid
      // showing a duplicate. React will re-render on the next page load using
      // localStorage (which cookie-consent.js writes on accept/reject).
      stampConsentMarkers(staticBanner)
      setVisible(false)
    } else {
      // Small delay so the banner doesn't flash during initial render
      const t = setTimeout(() => setVisible(true), 600)
      return () => clearTimeout(t)
    }
  }, [])

  if (!visible || consent !== null) return null

  const handleAcceptAll = () => {
    saveConsent('all')
    logConsentToServer('all')
    setConsent('all')
    setVisible(false)
  }

  const handleEssentialOnly = () => {
    saveConsent('essential')
    logConsentToServer('essential')
    setConsent('essential')
    setVisible(false)
  }

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-consent-banner=""
      className={cn(
        'fixed bottom-0 inset-x-0 z-50',
        'border-t border-[var(--brand-border-subtle)] bg-[var(--brand-bg)] backdrop-blur-sm shadow-lg',
        'animate-in slide-in-from-bottom-4 duration-300 motion-reduce:animate-none',
        className
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Icon + text */}
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Cookie className="h-5 w-5 text-brand-primary shrink-0 mt-0.5" />
          {/* Names the dialog in its text as well as its aria-label (the e2e gate tells the consent
              dialog from a navigation drawer by that text). */}
          <span className="sr-only">Cookie consent</span>
          <p className="text-sm text-brand-text-muted leading-relaxed">
            We use cookies to improve your experience and analyse site usage. Essential cookies are
            always active.{' '}
            <Link
              to="/cookies"
              className="text-brand-primary underline underline-offset-2 hover:opacity-80 transition-opacity whitespace-nowrap"
            >
              Cookie Policy
            </Link>
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <button
            onClick={handleEssentialOnly}
            className={cn(
              // min-h-11 / min-w-11: 44 px targets (WCAG 2.2 2.5.8 — the responsive gate measures them).
              'inline-flex min-h-11 min-w-11 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium',
              'border border-[var(--brand-border-subtle)] text-brand-text',
              'hover:bg-brand-surface-hover transition-colors'
            )}
          >
            <X className="h-3.5 w-3.5" />
            Essential only
          </button>
          <button
            onClick={handleAcceptAll}
            data-consent-accept=""
            className={cn(
              'inline-flex min-h-11 min-w-11 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium',
              'bg-brand-primary text-brand-text-on-primary',
              'hover:opacity-90 transition-opacity'
            )}
          >
            <Check className="h-3.5 w-3.5" />
            Accept all
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * useCookieConsent — hook to read the current consent value from localStorage.
 *
 * @returns {{ consent: 'all' | 'essential' | null }}
 *   null  = no decision yet (banner not yet dismissed)
 *   'essential' = user accepted essential cookies only
 *   'all' = user accepted all cookies
 */
export function useCookieConsent() {
  const [consent, setConsent] = useState(() => {
    const stored = loadConsent()
    return stored ? stored.value : null
  })

  useEffect(() => {
    const reread = () => {
      const stored = loadConsent()
      setConsent(stored ? stored.value : null)
    }
    // Re-read when storage changes (e.g. user opens second tab)
    /** @param {StorageEvent} e */
    function onStorage(e) {
      if (e.key === CONSENT_KEY) reread()
    }
    // …and when this tab's banner (React or /cookie-consent.js) records a choice.
    window.addEventListener('storage', onStorage)
    window.addEventListener(CONSENT_CHANGE_EVENT, reread)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(CONSENT_CHANGE_EVENT, reread)
    }
  }, [])

  return { consent }
}
